/**
 * Default: Modules > Static components (Playground, not for production server!!!)
 */

import {
	triggerReflow,
	getRootVar
} from '@ifabnpm/bootstrap/utils.js';

import SelectorEngine from '@ifabnpm/bootstrap/dom/selector-engine.js';
import Manipulator from '@ifabnpm/bootstrap/dom/manipulator.js';
import EventHandler from '@ifabnpm/bootstrap/dom/event-handler.js';

import Drawer   from '@ifabnpm/bootstrap/component/drawer.js';
import Jumplist from '@ifabnpm/bootstrap/component/jumplist.js';
import Reveal   from '@ifabnpm/bootstrap/component/reveal.js';

// -------
// Private
// -------

const exampleButton = () => {
	//
	// Loading
	//

	const loadingCollection = SelectorEngine.find('.static-components.-button button[data-loading]');
	const loadingToggle     = SelectorEngine.findOne('.static-components.-button button[data-loading-toggle]');

	if (loadingCollection && loadingToggle) {
		EventHandler.on(loadingToggle, 'click', (event) => {
			event.preventDefault();

			for (const element of loadingCollection) {
				Manipulator.toggleDataAttribute(element, 'loading');
			}
		});
	}

	//
	// Caret
	//

	const caretCollection = SelectorEngine.find('.static-components.-button button[data-caret]');
	const caretToggle     = SelectorEngine.findOne('.static-components.-button button[data-caret-toggle]');

	if (caretCollection && caretToggle) {
		EventHandler.on(caretToggle, 'click', (event) => {
			event.preventDefault();

			for (const element of caretCollection) {
				Manipulator.toggleDataAttribute(element, 'caret');
			}
		});
	}
};

const exampleButtonManipulations = () => {
	// Percent values decrease
	for (const btn of SelectorEngine.find('button[data-percent-decrease]')) {
		const id     = Manipulator.getDataAttribute(btn, 'percent-decrease');
		const target = SelectorEngine.findOne(`#${id}`);

		EventHandler.on(btn, 'click', (ev) => {
			ev.preventDefault();

			if(target) {
				Manipulator.setDataAttribute(target, 'value', Math.max(0, target.value - 10));
			}
		});
	}

	// Percent values increase
	for (const btn of SelectorEngine.find('button[data-percent-increase]')) {
		const id     = Manipulator.getDataAttribute(btn, 'percent-increase');
		const target = SelectorEngine.findOne(`#${id}`);

		EventHandler.on(btn, 'click', (ev) => {
			ev.preventDefault();

			if(target) {
				Manipulator.setDataAttribute(target, 'value', Math.min(100, target.value + 10));
			}
		});
	}

	// Toggle indeterminate
	for (const btn of SelectorEngine.find('button[data-toggle-indeterminate]')) {
		const id     = Manipulator.getDataAttribute(btn, 'toggle-indeterminate');
		const target = SelectorEngine.findOne(`#${id}`);

		EventHandler.on(btn, 'click', (ev) => {
			ev.preventDefault();

			if (target) {
				Manipulator.toggleDataAttribute(target, 'indeterminate');
			}
		});
	}
};

const exampleDrawer = () => {
	const topContainer = SelectorEngine.findOne(`.static-components.-drawer .drawer-top`);
	const topToggle    = SelectorEngine.findOne(`.static-components.-drawer button[data-drawer-top-toggle]`);

	if (topContainer && topToggle) {
		const topApi = new Drawer(topContainer);

		EventHandler.on(topToggle, 'click', (event) => {
			event.preventDefault();

			topApi.show();
		});
	}

	const bottomContainer = SelectorEngine.findOne(`.static-components.-drawer .drawer-bottom`);
	const bottomToggle    = SelectorEngine.findOne(`.static-components.-drawer button[data-drawer-bottom-toggle]`);

	if (bottomContainer && bottomToggle) {
		const bottomApi = new Drawer(bottomContainer);

		EventHandler.on(bottomToggle, 'click', (event) => {
			event.preventDefault();

			bottomApi.show();
		});
	}

	const startContainer = SelectorEngine.findOne(`.static-components.-drawer .drawer-start`);
	const startToggle    = SelectorEngine.findOne(`.static-components.-drawer button[data-drawer-start-toggle]`);

	if (startContainer && startToggle) {
		const startApi = new Drawer(startContainer);

		EventHandler.on(startToggle, 'click', (event) => {
			event.preventDefault();

			startApi.show();
		});
	}

	const endContainer = SelectorEngine.findOne(`.static-components.-drawer .drawer-end`);
	const endToggle    = SelectorEngine.findOne(`.static-components.-drawer button[data-drawer-end-toggle]`);

	if (endContainer && endToggle) {
		const endApi = new Drawer(endContainer);

		EventHandler.on(endToggle, 'click', (event) => {
			event.preventDefault();

			endApi.show();
		});
	}

	const endContainedContainer = SelectorEngine.findOne(`.static-components.-drawer .drawer-contained-end`);
	const endContainedToggle    = SelectorEngine.findOne(`.static-components.-drawer button[data-drawer-contained-end-toggle]`);

	if (endContainedToggle && endToggle) {
		const endContainedApi = new Drawer(endContainedContainer, {
			noHeader    : true,
			preventClose: true
		});

		EventHandler.on(endContainedToggle, 'click', (event) => {
			event.preventDefault();

			endContainedApi.show();
		});
	}
};

const exampleJumplist = () => {
	const apiJumplist1 = Jumplist.init(SelectorEngine.findOne('#jumplist-dev-1'));

	// Add
	EventHandler.on(
		SelectorEngine.findOne('#jumplist-dev-1-add'),
		'click',
		(event) => {
			event.preventDefault();

			const childs = SelectorEngine.children(apiJumplist1.getContainer(), '*');
			const nId    = childs.length + 1;

			Manipulator.elementAppend(
				`<div data-jumplist-name="Section ${nId}" id="jl-section-${nId}">
					<div>Section ${nId}<br/><br/><br/><br/><br/><br/><br/><br/><br/>...end section ${nId}</div>
				</div>`,
				apiJumplist1.getContainer()
			);

			apiJumplist1.refresh();
		}
	);

	// Remove
	EventHandler.on(
		SelectorEngine.findOne('#jumplist-dev-1-remove'),
		'click',
		(event) => {
			event.preventDefault();

			const childs = SelectorEngine.children(apiJumplist1.getContainer(), '*');

			if (childs.length > 0) {
				childs[childs.length - 1].remove();

				apiJumplist1.refresh();
			}
		}
	);
};

const exampleTag = () => {
	const removableCollection = SelectorEngine.find('.static-components.-tag .tag[data-removable]');
	const removableToggle     = SelectorEngine.findOne('.static-components.-tag button[data-tag-removable-toggle]');

	if (removableCollection && removableToggle) {
		for (const element of removableCollection) {
			EventHandler.on(element, `will-removed.ifab.tag`, (event) => {
				if (processEnv === 'development') {
					console.info('Tag will be removed', event.delegateTarget);
				}
			});

			EventHandler.on(element, `has-removed.ifab.tag`, (event) => {
				event.delegateTarget.hidden = false;

				if (processEnv === 'development') {
					console.info('Tag removed', event.delegateTarget);
				}

				setTimeout(() => {
					triggerReflow(event.delegateTarget);

					Manipulator.removeClass(event.delegateTarget, '-remove');
				}, 1000);
			});
		}

		EventHandler.on(removableToggle, 'click', (ev) => {
			ev.preventDefault();

			for (const element of removableCollection) {
				Manipulator.toggleDataAttribute(element, 'removable');
			}
		});
	}
};

const exampleReveal = () => {
	const container = SelectorEngine.findOne('.static-components.-reveal');

	if (container) {
		const controls = SelectorEngine.find(
			'[data-reveal-show], [data-reveal-hide], [data-reveal-toggle]',
			container
		);

		for (const element of controls) {
			const attrShow   = Manipulator.getDataAttribute(element, 'reveal-show');
			const attrHide   = Manipulator.getDataAttribute(element, 'reveal-hide');
			const attrToggle = Manipulator.getDataAttribute(element, 'reveal-toggle');

			let targets = [];

			if (attrShow) {
				targets = SelectorEngine.find(attrShow);
			} else if (attrHide) {
				targets = SelectorEngine.find(attrHide);
			} else if (attrToggle) {
				targets = SelectorEngine.find(attrToggle);
			}

			EventHandler.off(element, 'click');

			EventHandler.on(element, `click`, (event) => {
				event.preventDefault();

				if (targets.length > 0) {
					for (const target of targets) {
						if (attrShow) {
							Reveal.show(target).then(element => {
								if (processEnv === 'development') {
									console.info('Reveal shown', element);
								}
							});
						} else if (attrHide) {
							Reveal.hide(target).then(element => {
								if (processEnv === 'development') {
									console.info('Reveal hidden', element);
								}
							});
						} else if (attrToggle) {
							Reveal.toggle(target).then(element => {
								if (processEnv === 'development') {
									console.info('Reveal toggle', element);
								}
							});
						}
					}
				}
			});
		}
	}
};

// --------
// Let's go
// --------

document.addEventListener('DOMContentLoaded', function() {
	window.RootvarPrefix = window.RootvarPrefix || getRootVar('global-rootvar-prefix');

	exampleButton();
	exampleButtonManipulations();
	exampleDrawer();
	exampleJumplist();
	exampleTag();
	exampleReveal();
});
